import { Component, Host, h, Prop, Element, Watch, Build } from '@stencil/core';
import type {
  Product,
  SplideComponentConfig,
} from '../../helpers/utils/interfaces';
import {
  renderArticleImg,
  renderEnergyLabels,
  renderFormattedArticleName,
} from '../../helpers/helpers';
import Splide from '@splidejs/splide';
import { updateNextArrowState } from '../../helpers/splide-helper';

@Component({
  tag: 'cms-article-slider',
  styleUrl: 'cms-article-slider.scss',
  shadow: true,
})
export class CmsArticleSlider {
  private articles: Product[];

  private splideNode: HTMLElement;

  private splideObj: Splide;

  private splideTranslations: SplideComponentConfig;

  private readonly splideConfig = {
    type: 'slide',
    perMove: 1,
    gap: '0',
    autoWidth: true,
    slideFocus: false,
    pagination: false,
    trimSpace: true,
    autoHeight: true,
    focusableNodes: 'li',
    role: 'carousel',
  };

  /**
   * Set the Headline (optional)
   */
  @Prop() headline: string;

  /**
   * set the Products of the Slider
   */
  @Prop() products!: Product[] | string;

  /**
   * Set the tracking namespace (optional)
   */
  @Prop() namespace?: string;
  /**
   * Set the title which is used in the ratings
   */
  @Prop() ratingTitle = '';

  /**
   * Set the translations for splide
   */
  @Prop() translations: string;

  @Element() hostElement: HTMLElement;

  @Watch('products')
  private arrayProductsWatcher(newValue: Product[] | string) {
    if (newValue) {
      this.articles =
        typeof newValue === 'string'
          ? (JSON.parse(newValue) as Product[])
          : newValue;
    }
  }

  private mapArticles() {
    return this.articles.map((article: Product) => {
      const {
        detailsUrl,
        name,
        image,
        price,
        priceUnit,
        priceCurrency,
        bracketPrice,
        bracketPriceUnit,
        pricePrefix,
      } = article;
      return (
        <li
          key={name}
          class={'splide__slide article-slider-list'}
          {...{
            ariaHidden: false,
            tabIndex: 0,
          }}
        >
          <div class={'splide__slide__container'}>
            <a href={detailsUrl} class={'article-slider-cta'}>
              {this.renderArticleImageWrapper(name, image)}
              {this.renderArticleInfoWrapper(article)}
            </a>
            <cms-price
              price={price}
              unit={priceUnit}
              currency={priceCurrency}
              pricePrefix={pricePrefix}
              bracketPrice={bracketPrice}
              bracketPriceUnit={bracketPriceUnit}
            />
            {renderEnergyLabels(article)}
          </div>
        </li>
      );
    });
  }

  private renderArticleImageWrapper(name: string, image: string) {
    return (
      <div class={'article-image-wrapper'}>{renderArticleImg(name, image)}</div>
    );
  }

  private renderArticleInfoWrapper(article: Product) {
    const { name, ratingValue, ratingCount } = article;

    return (
      <div class={'article-info-wrapper'}>
        {name.length <= 40
          ? renderFormattedArticleName(name)
          : renderFormattedArticleName(this.shorterArticleName(name))}

        <cms-rating
          ratingValue={ratingValue}
          ratingCount={ratingCount}
          titleText={this.ratingTitle}
        />
      </div>
    ) as HTMLElement;
  }

  private shorterArticleName(name: string) {
    return `${name.substring(0, 40)} ...`;
  }

  private mountSplide() {
    if (!Build.isBrowser || this.products === undefined) {
      return;
    }
    this.splideObj = new Splide(this.splideNode, this.splideConfig);
    this.splideObj.on('ready resized visible', () =>
      updateNextArrowState(this.splideObj),
    );
    this.splideObj.mount();
  }

  componentWillLoad() {
    if (this.products !== undefined) {
      this.arrayProductsWatcher(this.products);
    }
    if (this.translations) {
      this.splideTranslations = JSON.parse(this.translations);
    }
  }

  componentDidLoad() {
    this.splideNode = this.hostElement.shadowRoot.querySelector('.splide');
    if (this.products !== undefined) {
      this.mountSplide();
    }
  }

  render() {
    return (
      <Host class={'cms-article-slider'}>
        {this.headline ? (
          <div class={'headline-wrapper'}>
            <cms-headline
              headlineText={this.headline}
              headlineTag='h2'
              headlinePosition='center'
              layout='full'
              headlineClass={'article-slider-headline'}
              isAdaptive={true}
            />
          </div>
        ) : null}
        <div class={'cms-article-slider-inner'}>
          <div class={'splide'}>
            <div class={'splide-inner'}>
              <div class='splide__arrows'>
                <cms-button
                  namespace={`${this.namespace}-arrow-left`}
                  class={'splide-arrow splide__arrow--prev'}
                  theme='secondary'
                  size='button-sm'
                  type='button'
                  icon='arrowLeft'
                  icon-size='sm'
                  tag-title={this.splideTranslations?.arrowLeftLabel}
                  focusable={false}
                />
                <cms-button
                  namespace={`${this.namespace}-arrow-right`}
                  class={'splide-arrow splide__arrow--next'}
                  theme='secondary'
                  size='button-sm'
                  type='button'
                  icon='arrowRight'
                  icon-size='sm'
                  tag-title={this.splideTranslations?.arrowRightLabel}
                  focusable={false}
                />
              </div>
              <div class={'splide__track'}>
                <ul class={'splide__list'}>{this.mapArticles()}</ul>
              </div>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
